import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Check, X, Trash2 } from 'lucide-react';
import { Transaction } from '@/api/transactions/use-transactions';
import { formatCurrency, formatDate } from '@/lib/format';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { useConfirm } from '@/hooks/useConfirm';
import { Link } from 'react-router-dom';

export interface TransactionDetailsModalProps {
    open: boolean;
    onClose: () => void;
    transaction: Transaction | null;
    /** Can approve/reject and edit notes */
    canEdit?: boolean;
    /** Can delete transaction */
    canDelete?: boolean;
    onApprove?: (transactionId: string) => Promise<void>;
    onReject?: (transactionId: string, rejectionReason: string) => Promise<void>;
    onDelete?: (transactionId: string) => Promise<void>;
    onUpdateNotes?: (transactionId: string, notes: string) => Promise<void>;
}

const TransactionDetailsModal: React.FC<TransactionDetailsModalProps> = ({
    open,
    onClose,
    transaction,
    canEdit = false,
    canDelete = false,
    onApprove,
    onReject,
    onDelete,
    onUpdateNotes,
}) => {
    const [notes, setNotes] = useState('');
    const [rejectionReasonInput, setRejectionReasonInput] = useState('');
    const [isEditingNotes, setIsEditingNotes] = useState(false);
    const [isSavingNotes, setIsSavingNotes] = useState(false);
    const [isActioning, setIsActioning] = useState(false);
    const confirm = useConfirm();

    useEffect(() => {
        if (transaction) {
            setNotes(transaction.notes ?? '');
            setRejectionReasonInput(transaction.rejection_reason ?? '');
        }
    }, [transaction]);

    if (!transaction) return null;

    const getTypeClassName = (type: string) => {
        switch (type) {
            case 'deposit':
                return 'bg-success/10 text-success border-success/20';
            case 'withdrawal':
            case 'withdraw':
                return 'bg-destructive/10 text-destructive border-destructive/20';
            case 'bonus':
                return 'bg-warning/10 text-warning border-warning/20';
            default:
                return 'bg-muted text-muted-foreground border-border';
        }
    };

    const formatTransactionDate = (dateString?: string) => {
        if (!dateString) return 'N/A';
        return formatDate(dateString, 'datetime');
    };

    const getCreatedByLabel = () => {
        const tx = transaction as Transaction & {
            created_by_type?: string;
            created_by_name?: string;
            metadata?: Record<string, any>;
        };

        if (tx.created_by_type === 'customer') {
            return 'customer';
        }

        if (tx.created_by_type === 'crm_user') {
            return tx.created_by_name || 'CRM User';
        }

        const source = tx.metadata?.source;
        if (source && source !== 'crm_manual') {
            return 'customer';
        }

        return tx.created_by_name || 'CRM User';
    };

    const isPending = transaction.status === 'pending';
    const showApproveReject = isPending && canEdit && onApprove && onReject;
    const showDelete = canDelete && onDelete;

    const handleSaveNotes = async () => {
        if (!onUpdateNotes) return;
        setIsSavingNotes(true);
        try {
            await onUpdateNotes(transaction.id, notes);
            setIsEditingNotes(false);
        } finally {
            setIsSavingNotes(false);
        }
    };

    const handleApprove = async () => {
        if (!onApprove) return;
        setIsActioning(true);
        try {
            await onApprove(transaction.id);
            onClose();
        } finally {
            setIsActioning(false);
        }
    };

    const handleReject = async () => {
        if (!onReject) return;
        const reason = rejectionReasonInput.trim() || undefined;
        if (!reason) {
            setRejectionReasonInput(prev => prev || ' ');
            return;
        }
        setIsActioning(true);
        try {
            await onReject(transaction.id, reason);
            onClose();
        } finally {
            setIsActioning(false);
        }
    };

    const handleDelete = async () => {
        if (!onDelete) return;
        const ok = await confirm({
            title: 'Delete transaction?',
            description:
                'Are you sure you want to delete this transaction? This action cannot be undone.',
            confirmLabel: 'Delete',
            variant: 'destructive',
        });
        if (!ok) return;
        setIsActioning(true);
        try {
            await onDelete(transaction.id);
            onClose();
        } finally {
            setIsActioning(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-3">
                        Transaction Details
                        <Badge variant="outline" className={getTypeClassName(transaction.type)}>
                            {transaction.type.toUpperCase()}
                        </Badge>
                        <StatusBadge status={transaction.status} kind="transaction" />
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <div className="bg-muted/50 p-4 rounded-lg">
                                <h3 className="font-medium mb-3">Transaction Information</h3>
                                <div className="space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">ID:</span>
                                        <span className="text-sm font-medium">
                                            {transaction.id}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">Type:</span>
                                        <Badge
                                            variant="outline"
                                            className={getTypeClassName(transaction.type)}
                                        >
                                            {transaction.type.toUpperCase()}
                                        </Badge>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Status:
                                        </span>
                                        <StatusBadge
                                            status={transaction.status}
                                            kind="transaction"
                                        />
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Amount:
                                        </span>
                                        <span className="text-sm font-medium">
                                            {formatCurrency(
                                                transaction.amount,
                                                transaction.currency
                                            )}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Currency:
                                        </span>
                                        <span className="text-sm font-medium">
                                            {transaction.currency}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-muted/50 p-4 rounded-lg">
                                <h3 className="font-medium mb-3">Payment Details</h3>
                                <div className="space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Payment Method:
                                        </span>
                                        <span className="text-sm font-medium">
                                            {transaction.payment_method || 'N/A'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Reference Number:
                                        </span>
                                        <span className="text-sm font-medium">
                                            {transaction.reference_number || 'N/A'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Description:
                                        </span>
                                        <span translate="no" className="text-sm font-medium">
                                            {transaction.description || 'N/A'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="bg-muted/50 p-4 rounded-lg">
                                <h3 className="font-medium mb-3">Account Information</h3>
                                <div className="space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Customer:
                                        </span>
                                        <span className="text-sm font-medium">
                                            {transaction.customer?.id ? (
                                                <Link
                                                    to={`/customers/${transaction.customer.id}`}
                                                    className="text-primary hover:underline"
                                                >
                                                    {transaction.customer?.name || 'N/A'}
                                                </Link>
                                            ) : (
                                                transaction.customer?.name || 'N/A'
                                            )}
                                            {transaction.customer?.customer_number && (
                                                <span className="ms-2 text-xs text-primary font-mono">
                                                    (#{transaction.customer.customer_number})
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Trading Account:
                                        </span>
                                        <span className="text-sm font-medium">
                                            {transaction.trading_account?.base_currency || 'N/A'}
                                            {transaction.trading_account?.account_number && (
                                                <span className="ms-2 text-xs text-success font-mono">
                                                    (#{transaction.trading_account.account_number})
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    {transaction.trading_account && (
                                        <div className="flex justify-between">
                                            <span className="text-sm text-muted-foreground">
                                                Account Currency:
                                            </span>
                                            <span className="text-sm font-medium">
                                                {transaction.trading_account.base_currency}
                                            </span>
                                        </div>
                                    )}
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Created By:
                                        </span>
                                        <span className="text-sm font-medium">
                                            {getCreatedByLabel()}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-muted/50 p-4 rounded-lg">
                                <h3 className="font-medium mb-3">Timestamps</h3>
                                <div className="space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Created At:
                                        </span>
                                        <span className="text-sm font-medium">
                                            {formatTransactionDate(transaction.created_at)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            Updated At:
                                        </span>
                                        <span className="text-sm font-medium">
                                            {formatTransactionDate(transaction.updated_at)}
                                        </span>
                                    </div>
                                    {transaction.approved_at && (
                                        <div className="flex justify-between">
                                            <span className="text-sm text-muted-foreground">
                                                Approved At:
                                            </span>
                                            <span className="text-sm font-medium">
                                                {formatTransactionDate(transaction.approved_at)}
                                            </span>
                                        </div>
                                    )}
                                    {transaction.rejected_at && (
                                        <div className="flex justify-between">
                                            <span className="text-sm text-muted-foreground">
                                                Rejected At:
                                            </span>
                                            <span className="text-sm font-medium">
                                                {formatTransactionDate(transaction.rejected_at)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {transaction.approved_by && (
                        <div className="bg-muted/50 p-4 rounded-lg">
                            <h3 className="font-medium mb-3">Approval Information</h3>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-sm text-muted-foreground">
                                        Approved By:
                                    </span>
                                    <span className="text-sm font-medium">
                                        {transaction.approved_by}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-muted-foreground">
                                        Approved At:
                                    </span>
                                    <span className="text-sm font-medium">
                                        {formatTransactionDate(transaction.approved_at)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {transaction.rejected_by && (
                        <div className="bg-muted/50 p-4 rounded-lg">
                            <h3 className="font-medium mb-3">Rejection Information</h3>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-sm text-muted-foreground">
                                        Rejected By:
                                    </span>
                                    <span className="text-sm font-medium">
                                        {transaction.rejected_by}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-muted-foreground">
                                        Rejected At:
                                    </span>
                                    <span className="text-sm font-medium">
                                        {formatTransactionDate(transaction.rejected_at)}
                                    </span>
                                </div>
                                {transaction.rejection_reason && (
                                    <div className="flex flex-col gap-1">
                                        <span className="text-sm text-muted-foreground">
                                            Rejection Reason:
                                        </span>
                                        <span className="text-sm font-medium">
                                            {transaction.rejection_reason}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Notes: show + add/edit when canEdit */}
                    <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                        <h3 className="font-medium">Notes</h3>
                        {isEditingNotes ? (
                            <>
                                <Textarea
                                    value={notes}
                                    onChange={e => setNotes(e.target.value)}
                                    placeholder="Add or edit notes..."
                                    rows={4}
                                    className="resize-none"
                                />
                                <div className="flex gap-2">
                                    <Button
                                        size="sm"
                                        onClick={handleSaveNotes}
                                        disabled={isSavingNotes}
                                    >
                                        {isSavingNotes ? 'Saving...' : 'Save notes'}
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                            setIsEditingNotes(false);
                                            setNotes(transaction.notes ?? '');
                                        }}
                                        disabled={isSavingNotes}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <>
                                <p className="text-sm text-foreground whitespace-pre-wrap">
                                    {notes || 'No notes.'}
                                </p>
                                {canEdit && onUpdateNotes && (
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setIsEditingNotes(true)}
                                    >
                                        {notes ? 'Edit notes' : 'Add notes'}
                                    </Button>
                                )}
                            </>
                        )}
                    </div>

                    {/* Rejection reason: show when rejected; when pending + canEdit, show input for use when rejecting */}
                    {isPending && canEdit && onReject && (
                        <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                            <Label htmlFor="rejection-reason">
                                Rejection reason (required when rejecting)
                            </Label>
                            <Textarea
                                id="rejection-reason"
                                value={rejectionReasonInput}
                                onChange={e => setRejectionReasonInput(e.target.value)}
                                placeholder="Enter reason for rejection..."
                                rows={3}
                                className="resize-none"
                            />
                        </div>
                    )}
                </div>

                <DialogFooter className="flex flex-wrap gap-2 sm:justify-between">
                    <div className="flex flex-wrap gap-2">
                        {showApproveReject && (
                            <>
                                <Button
                                    variant="success"
                                    onClick={handleApprove}
                                    disabled={isActioning}
                                >
                                    <Check className="h-4 w-4" />
                                    Approve
                                </Button>
                                <Button
                                    variant="destructive"
                                    onClick={handleReject}
                                    disabled={isActioning || !rejectionReasonInput.trim()}
                                >
                                    <X className="h-4 w-4" />
                                    Reject
                                </Button>
                            </>
                        )}
                        {showDelete && (
                            <Button
                                variant="destructive"
                                onClick={handleDelete}
                                disabled={isActioning}
                            >
                                <Trash2 className="h-4 w-4" />
                                Delete
                            </Button>
                        )}
                    </div>
                    <Button variant="outline" onClick={onClose}>
                        Close
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default TransactionDetailsModal;
